/**
 * Helpers for notion-editor-link-patch.js.
 */
import {
  findEditorLinkAnchor,
  openLinkAnchorReadonly,
  selectLinkAtAnchor,
} from "@components/TiptapEditor/linkEditorUtils.js";

const EDITOR_PROBE_KEYS = new Set([
  "chain",
  "commands",
  "getAttributes",
  "isActive",
  "state",
  "view",
]);

function isTiptapEditor(candidate, proseMirrorDom) {
  return (
    candidate?.view?.dom === proseMirrorDom &&
    typeof candidate.chain === "function" &&
    typeof candidate.getAttributes === "function"
  );
}

export function findEditorInVueTree(root, proseMirrorDom, maxDepth = 6) {
  if (!root || maxDepth < 0) {
    return null;
  }

  if (isTiptapEditor(root, proseMirrorDom)) {
    return root;
  }

  if (typeof root !== "object") {
    return null;
  }

  const values = root.value && typeof root.value === "object" ? [root.value] : [];
  for (const key of Object.keys(root)) {
    if (!EDITOR_PROBE_KEYS.has(key)) {
      values.push(root[key]);
    }
  }

  for (const value of values) {
    if (!value || typeof value !== "object") {
      continue;
    }
    if (isTiptapEditor(value, proseMirrorDom)) {
      return value;
    }
    const nested = findEditorInVueTree(value, proseMirrorDom, maxDepth - 1);
    if (nested) {
      return nested;
    }
  }

  return null;
}

export function resolveEditorFromNotionHost(host, proseMirrorDom) {
  const instance = host?._instance || host?.__vueParentComponent;
  if (!instance) {
    return null;
  }

  for (const bag of [instance.setupState, instance.ctx, instance.proxy, instance.exposed, instance]) {
    if (!bag) {
      continue;
    }
    const editor = findEditorInVueTree(bag, proseMirrorDom);
    if (editor) {
      return editor;
    }
  }

  return null;
}

export function resolveEditorFromProseMirror(proseMirrorDom) {
  if (!proseMirrorDom) {
    return null;
  }

  const cached = proseMirrorDom.__exiqTiptapEditor;
  if (cached?.view?.dom === proseMirrorDom) {
    return cached;
  }

  let root = proseMirrorDom.getRootNode?.();
  while (root && root !== document) {
    const host = root.host;
    if (host?.tagName === "EXIQ-NOTION-EDITOR") {
      const editor = resolveEditorFromNotionHost(host, proseMirrorDom);
      if (editor) {
        proseMirrorDom.__exiqTiptapEditor = editor;
        return editor;
      }
    }
    root = host?.getRootNode?.();
  }

  return null;
}

export function resolveFocusedNotionEditor() {
  for (const host of document.querySelectorAll("exiq-notion-editor")) {
    const proseMirror =
      host.shadowRoot?.querySelector(".ProseMirror-focused") ||
      host.shadowRoot?.querySelector(".ProseMirror");
    const editor = proseMirror ? resolveEditorFromProseMirror(proseMirror) : null;
    if (editor) {
      return editor;
    }
  }
  return null;
}

export function bubbleMenuHasLinkButton(bubbleInner) {
  return [...(bubbleInner?.querySelectorAll(".inline-bubble-button") || [])].some(
    (button) => button.textContent.trim() === "Link"
  );
}

function activateBubbleLink(event, onActivate) {
  event.preventDefault();
  event.stopPropagation();
  onActivate();
}

export function createBubbleLinkButton({ onActivate }) {
  const linkBtn = document.createElement("button");
  linkBtn.type = "button";
  linkBtn.className = "inline-bubble-button";
  linkBtn.textContent = "Link";
  linkBtn.setAttribute("aria-label", "Add link");
  linkBtn.addEventListener("mousedown", (event) => activateBubbleLink(event, onActivate));
  linkBtn.addEventListener("click", (event) => activateBubbleLink(event, onActivate));
  return linkBtn;
}

export function isBubbleLinkButton(target) {
  const button = target?.closest?.(".inline-bubble-button");
  return button instanceof HTMLButtonElement && button.textContent.trim() === "Link";
}

export function handleBubbleLinkButtonClick(event, shadowRoot, openLinkForEditor) {
  if (event.button !== 0 || !isBubbleLinkButton(event.target)) {
    return false;
  }

  const proseMirror = shadowRoot.querySelector(".ProseMirror");
  const editor = resolveEditorFromProseMirror(proseMirror) || resolveFocusedNotionEditor();
  if (!editor) {
    return false;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  window.setTimeout(() => openLinkForEditor(editor), 0);
  return true;
}

export function handleEditorLinkAnchorClick(event, editorDom, openLinkForEditor) {
  if (event.button !== 0) {
    return false;
  }

  const anchor = findEditorLinkAnchor(event.target, editorDom);
  if (!anchor) {
    return false;
  }

  if (editorDom.getAttribute("contenteditable") === "false") {
    openLinkAnchorReadonly(event, anchor);
    return true;
  }

  const editor = resolveEditorFromProseMirror(editorDom);
  if (!editor) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  selectLinkAtAnchor(editor, anchor);
  window.setTimeout(() => openLinkForEditor(editor), 0);
  return true;
}

export function injectBubbleLinkButton(shadowRoot, openLinkForEditor) {
  const bubbleInner = shadowRoot.querySelector(".inline-bubble-inner");
  if (!bubbleInner || bubbleMenuHasLinkButton(bubbleInner)) {
    return false;
  }

  const strikeBtn = [...bubbleInner.querySelectorAll(".inline-bubble-button")].find((button) => {
    const label = button.textContent.trim();
    return label === "Strike" || label === "Strikethrough";
  });
  const proseMirror = shadowRoot.querySelector(".ProseMirror");
  if (!strikeBtn || !proseMirror) {
    return false;
  }

  const linkBtn = createBubbleLinkButton({
    onActivate: () => {
      const editor = resolveEditorFromProseMirror(proseMirror);
      if (editor) {
        openLinkForEditor(editor);
      }
    },
  });

  const dividerAfterStrike = strikeBtn.nextElementSibling;
  if (dividerAfterStrike?.classList.contains("inline-bubble-divider")) {
    dividerAfterStrike.after(linkBtn);
  } else {
    strikeBtn.after(linkBtn);
  }

  return true;
}

export function bindBubbleLinkButtonHandler(shadowRoot, openLinkForEditor) {
  if (!shadowRoot || shadowRoot.__exiqBubbleLinkClickBound) {
    return;
  }

  shadowRoot.__exiqBubbleLinkClickBound = true;
  const onPointer = (event) => handleBubbleLinkButtonClick(event, shadowRoot, openLinkForEditor);
  shadowRoot.addEventListener("mousedown", onPointer, true);
  shadowRoot.addEventListener("click", onPointer, true);
}

export function bindBubbleMenuWatcher(shadowRoot, patchBubbleMenu) {
  const bubbleMenu = shadowRoot.querySelector(".inline-bubble.custom-floating-menu");
  if (!bubbleMenu || bubbleMenu.__exiqBubbleMenuWatcher) {
    return;
  }

  bubbleMenu.__exiqBubbleMenuWatcher = true;
  patchBubbleMenu(shadowRoot);

  new MutationObserver(() => patchBubbleMenu(shadowRoot)).observe(bubbleMenu, {
    attributes: true,
    attributeFilter: ["style", "class"],
    childList: true,
    subtree: true,
  });
}
